import './DuplicateModal.scss';
import { useContext, useEffect } from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import { MdWarning } from 'react-icons/md';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { AlertDialog } from '@Components/AlertDialog';
import { Intent } from '@Components/designConstants';
import { useSetError } from '../../../redux/modals';
import { WtoLambdaFetch } from '../../../utils/fetch-utils';
import { formValidator } from '../Validation/wto-form-validator';
import { FormContext, formObjDefault } from '../wto-form-context';
import {
  formatDefaultId,
  formatRules,
  trimBackslashes,
  updateParamFunnelStep,
  updateParamTestName,
  updateParamVersionNumber
} from '../wto-utils';

interface DuplicateModalProps {
  show: boolean;
  closeModal: () => void;
  data: Plover;
  userEmail: string;
  companyId: string;
  fetchPlovers: () => void;
}

export const DuplicateModal = ({
  show,
  closeModal,
  data,
  userEmail,
  companyId,
  fetchPlovers
}: DuplicateModalProps) => {
  const { wtoFormObj, setWtoFormObj } = useContext(FormContext);

  const setError = useSetError();

  useEffect(() => {
    if (show) {
      setWtoFormObj(() => ({
        company: companyId,
        created_by: userEmail,
        default_id: data.default_id,
        default_url: data.default_url,
        domain: data.domain,
        folder_path: data.folder_path,
        notes: data.notes,
        plover_url: data.plover_url,
        rules: data.rules,
        status: 'DRAFT',
        test_name: '',
        utm_test_id: '',
        variants: data.variants
      }));
    }
  }, [show, data, setWtoFormObj, userEmail, companyId]);

  const cancelDuplication = () => {
    setWtoFormObj({ ...formObjDefault });
    closeModal();
  };

  const confirmDuplication = async () => {
    try {
      await WtoLambdaFetch('/plover', {
        method: 'POST',
        body: JSON.stringify({
          ...wtoFormObj,
          default_id: formatDefaultId(wtoFormObj.default_id),
          rules: formatRules(wtoFormObj.rules, true),
          folder_path: trimBackslashes(wtoFormObj.folder_path)
        })
      });
      fetchPlovers();
    } catch (e) {
      setError({
        message: `Failed to duplicate plover. Error: ${e.message}`,
        reportError: e
      });
    }
  };

  const validateForm = () => {
    const errors = formValidator(wtoFormObj, ['test_name', 'utm_test_id'], {
      isBulkPlover: false
    });
    if (errors.length > 0) {
      setError({ title: 'Validation Error(s)', message: `• ${errors.join('\n• ')}` });
    } else {
      confirmDuplication();
    }
  };

  const updateFormField = (key, value) => setWtoFormObj(current => ({ ...current, [key]: value }));

  return (
    <AlertDialog
      show={show}
      onHide={cancelDuplication}
      intent={Intent.WARNING}
      icon={<MdWarning size={24} />}
      title='Duplicate Route'
      className='duplicate-modal'
      body={
        <>
          <p className='duplicate-intro'>
            Before duplicating the test <span>{data?.test_name}</span>, please enter unique values
            for the fields below:
          </p>
          <Form className='form'>
            <Row>
              <Form.Group as={Col}>
                <Form.Label>Route Name</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Route Name. e.g. Quiz CTA Test'
                  onChange={e => updateFormField('test_name', e.target.value)}
                />
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col}>
                <Form.Label>Route Tracking URL Parameter</Form.Label>
                <p className='helper-text'>
                  Former Client Example: utm_home_exp=home4
                  <br />
                  <br />
                  Live Preview:{' '}
                </p>
                <InputGroup>
                  <Form.Control
                    type='text'
                    placeholder='Funnel Step'
                    onChange={e =>
                      updateFormField(
                        'utm_test_id',
                        updateParamFunnelStep(e.target.value, wtoFormObj.utm_test_id)
                      )
                    }
                  />
                  <Form.Control
                    type='text'
                    placeholder='Test Name'
                    onChange={e =>
                      updateFormField(
                        'utm_test_id',
                        updateParamTestName(e.target.value, wtoFormObj.utm_test_id)
                      )
                    }
                  />
                  {/* TODO(DEV-24309): no input for a suffix yet -- see wto-utils.ts. */}
                  <Form.Control
                    type='number'
                    placeholder='Version'
                    onChange={e =>
                      updateFormField(
                        'utm_test_id',
                        updateParamVersionNumber(Number(e.target.value), wtoFormObj.utm_test_id)
                      )
                    }
                  />
                </InputGroup>
              </Form.Group>
            </Row>
          </Form>
        </>
      }
      primaryAction={{ onClick: validateForm, label: 'Confirm' }}
      secondaryAction={{ onClick: cancelDuplication }}
      passthroughProps={{ 'data-testid': 'duplicate-modal' }}
    />
  );
};
